import {Cell, Row, Worksheet} from 'exceljs';
import {COLUMN_CAPTIONS, ColumnTypes} from 'src/modules/change-requests/constants';
import {getCriteriaRichText} from 'src/modules/change-requests/download/criteria/get-criteria';
import {Filters} from 'src/modules/change-requests/typedefs';
import {MAPPED_CHANGE_REQUEST_STATUS} from 'src/modules/constants';
import {
    decorateCell,
    decorateLinkCell,
    decorateSheetTitleCell,
    decorateTableCell,
    decorateTableHeaderCell,
    setCellValue,
} from 'src/modules/downloads/cells/cells';
import {DEFAULT_DATE_FORMAT, HorizontalAlignment, VerticalAlignment} from 'src/modules/downloads/cells/styles';
import {printLogo} from 'src/modules/downloads/commons/print-logo';
import {printMetadata} from 'src/modules/downloads/commons/print-metadata';
import {getUserName} from 'src/modules/downloads/utils/get-user-name';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {User} from 'src/openapi/models/User';

import {formatDate} from '@pb/kit-formatters';

const FIRST_ROW = 1;
const FIRST_ROW_HEIGHT = 30;
const FIRST_DATA_ROW = 10;
const TABLE_HEADER_ROW = 9;
const TABLE_HEADER_HEIGHT = 35;
const SHEET_TITLE = 'Change Requests';
const LINK = 'Link:';
const CRITERIA = 'Criteria:';
const CHANGE_REQUEST_LINK_CAPTION = 'View Change Requests';

const printCriteria = (worksheet: Worksheet, filters: Filters) => {
    worksheet.mergeCells('B4:D6');
    const cell = worksheet.getCell('B4') as Partial<Cell>;
    const richText = getCriteriaRichText(filters);
    cell.alignment = {vertical: VerticalAlignment.TOP, wrapText: true};
    cell.value = {richText};
};

export const printSheetHeader = (worksheet: Worksheet, userName: string, filters: Filters) => {
    const firstRow = worksheet.getRow(FIRST_ROW) as Partial<Row>;
    firstRow.height = FIRST_ROW_HEIGHT;

    const linkCaption = (cell: Partial<Cell>) =>
        cell.value = {text: CHANGE_REQUEST_LINK_CAPTION, hyperlink: window.location.href};

    printLogo(worksheet);
    printMetadata(worksheet, userName);
    printCriteria(worksheet, filters);
    decorateSheetTitleCell(worksheet.getCell('D1'), setCellValue(SHEET_TITLE, HorizontalAlignment.CENTER));
    decorateCell(worksheet.getCell('A3'), setCellValue(LINK, HorizontalAlignment.RIGHT));
    decorateLinkCell(worksheet.getCell('B3'), linkCaption);
    decorateCell(worksheet.getCell('A4'), setCellValue(CRITERIA, HorizontalAlignment.RIGHT, VerticalAlignment.TOP));
};

export const printTableDataHeader = (worksheet: Worksheet) => {
    const row = worksheet.getRow(TABLE_HEADER_ROW) as Partial<Row>;
    row.height = TABLE_HEADER_HEIGHT;

    const type = COLUMN_CAPTIONS[ColumnTypes.EVENT_TYPE];
    const source = COLUMN_CAPTIONS[ColumnTypes.EVENT_SOURCE];
    const code = COLUMN_CAPTIONS[ColumnTypes.EVENT_CODE];
    const description = COLUMN_CAPTIONS[ColumnTypes.EVENT_DESCRIPTION];
    const author = COLUMN_CAPTIONS[ColumnTypes.AUTHOR];
    const status = COLUMN_CAPTIONS[ColumnTypes.STATUS];
    const lastUpdateDate = COLUMN_CAPTIONS[ColumnTypes.LAST_UPDATED];
    const dueDate = COLUMN_CAPTIONS[ColumnTypes.DUE_DATE];

    decorateTableHeaderCell(worksheet.getCell('A9'), 15, setCellValue(type, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('B9'), 20, setCellValue(source, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('C9'), 30, setCellValue(code, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('D9'), 85, setCellValue(description, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('E9'), 30, setCellValue(author, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('F9'), 20, setCellValue(status, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('G9'), 20, setCellValue(lastUpdateDate, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('H9'), 20, setCellValue(dueDate, HorizontalAlignment.CENTER));
};

export const printTableData = (
    worksheet: Worksheet,
    changeRequests: Array<ChangeRequest>,
    usersInfo: Record<string, User>,
) => {
    changeRequests.forEach((changeRequest, index) => {
        const row = worksheet.getRow(FIRST_DATA_ROW + index);

        const event = changeRequest.event;
        const authorEmail = changeRequest.authorEmail;
        const userName = getUserName(authorEmail, usersInfo[authorEmail]);
        const status = MAPPED_CHANGE_REQUEST_STATUS[changeRequest.status];
        const lastUpdateDate = formatDate(changeRequest.updateDate, DEFAULT_DATE_FORMAT);
        const dueDate = formatDate(changeRequest.dueDate, DEFAULT_DATE_FORMAT);

        decorateTableCell(row, 1, setCellValue(event.type));
        decorateTableCell(row, 2, setCellValue(event.source));
        decorateTableCell(row, 3, setCellValue(event.code));
        decorateTableCell(row, 4, setCellValue(event.description));
        decorateTableCell(row, 5, setCellValue(userName));
        decorateTableCell(row, 6, setCellValue(status, HorizontalAlignment.LEFT));
        decorateTableCell(row, 7, setCellValue(lastUpdateDate, HorizontalAlignment.CENTER));
        decorateTableCell(row, 8, setCellValue(dueDate, HorizontalAlignment.CENTER));
    });
};
